import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import * as thunks from './thunks';
import * as actions from './actions';
import selectors from './selectors';
import adapter from './adapter';
import { lineTotal } from 'utils/cartQuantity';

/* Through the same `lineTotal` the screen subtracts by, so the running total
   and the recounted one cannot disagree about a fractional price. */
const calculateCartTotal = (items) => items.reduce((acc, item) => acc + lineTotal(item), 0);

const SIGN_IN_REQUIRED = 'You need to sign in or sign up before continuing.';

/* The API refuses an order with `{errors: [...]}` but a single line with a hash
   keyed by the field it refused - `{quantity: ['الكمية غير صحيحة']}`. Only the
   first shape was ever read, so every refused line showed the generic "something
   went wrong" in place of the Arabic reason the API had already written. Both are
   flattened to the list of messages behind them, and a 401's `{error: '...'}`
   comes through the same way so it can still be recognised below. */
/* A refusal the API wrote is a sentence for the client to read. Anything past
   this is not one, and the only thing on the other side of it is a page that
   was never meant for them. */
const LONGEST_REFUSAL = 300;

/* Only what the API meant as a message. A 500 is answered with Rails' own error
   page, and axios hands that whole document over as the payload the same way it
   hands over a refusal - so, read as a bare string, the client got a modal
   full of `<!DOCTYPE html>` and the stack trace behind it. There is nothing in
   there they can act on, so it falls back to the generic failure instead. */
const isReadableMessage = (text) => text.length > 0 && text.length <= LONGEST_REFUSAL && !/^[<{[]/.test(text);

const errorMessages = (payload) => {
  if (!payload) return [];
  /* A line the invoice would not take is answered with the exception itself,
     which renders as a bare JSON string rather than any of the keyed shapes.
     Taken to `Object.values` below, a string comes back split into its own
     characters and the refusal renders one letter at a time. */
  if (typeof payload === 'string') {
    const text = payload.trim();
    return isReadableMessage(text) ? [text] : [];
  }

  const values = Array.isArray(payload) ? payload : Object.values(payload);

  return values
    .reduce((acc, value) => acc.concat(value), [])
    .filter((value) => typeof value === 'string' && isReadableMessage(value.trim()))
    .map((value) => value.trim());
};

/* The reason the API gave for refusing, as one sentence, or null when it gave
   none of its own - either because it wrote nothing readable, or because this is
   devise answering an expired session, which the client is already being sent to
   the sign-in screen for and is not also told about here.

   The messages are joined rather than handed back as a list, because whatever
   reads this puts it in a sentence's place - and a list of two interpolates as
   `first,second`, which the client reads as two refusals run together with no
   space between them. The API refuses per field, so two at once is only a matter
   of a line being wrong in two ways. Joined the way it punctuates its own
   Arabic.

   Exported for the scheduling dialog, which carries the refusal itself rather
   than through the cart's alert, and so needs the message without the key the
   alert falls back to - it renders what it is given as text. */
export const rejectionMessage = (payload) => {
  const messages = errorMessages(payload);
  if (!messages.length || messages.includes(SIGN_IN_REQUIRED)) return null;

  /* Measured again on what is actually shown, not only on each message before
     it was joined: two refusals each just inside the limit come to twice it,
     and the limit is about what the client ends up reading. */
  const joined = messages.join('، ');

  return isReadableMessage(joined) ? joined : null;
};

/* The alert a refused cart request should show, or null when it should show
   none - the expired session above.

   Exported because a screen recovering from a refused write has to put the same
   alert back by hand - a recovery refetch clears it on the way past, the way any
   load does. One function so the message the client reads is decided once. */
export const alertFromRejection = (payload) => {
  if (errorMessages(payload).includes(SIGN_IN_REQUIRED)) return null;

  /* The fallback is a key rather than a sentence: nothing readable came back,
     so the client is told so in their own language. */
  return { message: rejectionMessage(payload) || 'something went wrong', type: 'error' };
};

const setRejectionAlert = (state, action) => {
  const alert = alertFromRejection(action.payload);
  if (alert) state.alert = alert;
};

const slice = createSlice({
  name: 'Cart',
  initialState: adapter.getInitialState({
    loading: false,
    /* Whether a load of the cart is in the air, which is narrower than
       `loading` - that is raised by writes too. Read by the fetch thunk's own
       condition so two screens asking for the cart at once only ask once. */
    fetching: false,
    alert: null,
    total: 0,
  }),
  extraReducers: (builder) => {
    builder
      .addCase(actions.setAlert, (state, action) => {
        state.alert = action.payload;
      })
      .addCase(thunks.addItems.pending, (state, action) => {
        const { loader } = action.meta.arg;
        if (loader) {
          state.loading = true;
          state.alert = null;
        }
      })
      .addCase(thunks.fetch.fulfilled, (state, action) => {
        state.loading = false;
        state.fetching = false;
        adapter.setAll(state, action.payload);
        state.total = calculateCartTotal(action.payload);
      })
      /* A failed *load* is the one rejection that does empty the cart, because
         then there is genuinely nothing to show - unlike a refused write, below,
         which leaves the cart exactly as it was. */
      .addCase(thunks.fetch.rejected, (state, action) => {
        state.loading = false;
        state.fetching = false;
        setRejectionAlert(state, action);
        state.total = 0;
        adapter.removeAll(state);
      })
      .addCase(thunks.addItems.fulfilled, (state, action) => {
        const { loader } = action.meta.arg;
        adapter.setAll(state, action.payload.cart_items);
        state.total = calculateCartTotal(action.payload.cart_items);
        if (loader) {
          state.loading = false;
          state.alert = { message: 'items added successfully', type: 'success', subtitle: 'go to cart page' };
        }
      })
      .addCase(thunks.removeItems.fulfilled, (state, action) => {
        const { ids, cancel, total } = action.meta.arg;
        state.loading = false;
        if (cancel) {
          state.alert = { message: 'order canceled successfully', type: 'success' };
          state.total = 0;
        } else {
          state.total -= total || 0;
        }
        adapter.removeMany(state, ids);
      })
      .addCase(thunks.submitCart.fulfilled, (state, action) => {
        state.loading = false;
        state.alert = { message: 'submitted successfully', type: 'success' };
        adapter.removeAll(state);
        state.total = 0;
      })
      .addCase(thunks.fetch.pending, (state) => {
        state.fetching = true;
      })
      .addMatcher(
        isAnyOf(thunks.fetch.pending, thunks.submitCart.pending, thunks.removeItems.pending),
        (state, action) => {
          state.loading = true;
          state.alert = null;
        },
      )
      /* A write that was refused leaves the cart exactly as it was. It used to
         empty the store on any rejection, so a single refused line - a quantity
         the API would not take, a part no longer available - blanked the whole
         screen while the API still held every item, and the client was left
         looking at an empty cart and a generic error. Only a failed *load*
         empties it, above, because then there is genuinely nothing to show. */
      .addMatcher(
        isAnyOf(thunks.submitCart.rejected, thunks.removeItems.rejected, thunks.addItems.rejected),
        (state, action) => {
          state.loading = false;
          setRejectionAlert(state, action);
        },
      );
  },
});

const cart = {
  slice,
  thunks,
  actions,
  selectors,
  alertFromRejection,
  rejectionMessage,
};
export default cart;
